<template>
  <ServerError v-if="isInternalError"/>
  <NotFoundError v-else-if="isNotFound"/>
  <Forbidden403 v-else-if="isForbidden"/>
  <div v-else>
    <component :is="layoutComponent">
      <AdminBar v-if="showAdminBar && isAdmin"/>
      <router-view />
    </component>
  </div>
  <Drawer v-model:visible="contentModalVisible" @hide="closeContentModal" :position="smallScreenSize ? 'full' : 'right'" :block-scroll="true">
    <ContentModal />
  </Drawer>
  <Toast/>

  <PiniaColadaDevtools />
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onErrorCaptured, onMounted, ref } from "vue";
import pinia from "@/plugins/PiniaPlugin";
import { PiniaColadaDevtools } from '@pinia/colada-devtools';

import { useWindowSize } from '@vueuse/core'

import PublicLayout from "@/layouts/Public.vue";
import PrivateLayout from "@/layouts/Private.vue";
import Forbidden403 from '@/pages/Forbidden403.vue';
import ServerError from '@/components/errors/ServerError.vue';
import NotFoundError from "@/components/errors/NotFoundError.vue";
import AdminBar from '@/components/layout/AdminBar.vue';
import ContentModal from "@/components/modals/ContentModal.vue";

import { useApplicationStore } from "@/stores/Application";
import { useApplicationAlertsStore } from "@/stores/ApplicationAlerts";
import { storeToRefs } from "pinia";

import browserUpdate, { Options } from 'browser-update';
import { useAuthStore } from "@/stores/Auth";
import { clientStateManager } from "@/services/error-services";
import { ConfigurationError } from "@/types/errors";

const options: Options = {
  api: 2024.10,
  required: { e: -2, f: -2, o: 0, s: -1, c: -4 },
  insecure: true,
  mobile: true,
  newwindow: true,
  no_permanent_hide: true,
  shift_page_down: false,
  text: {
    msg: 'Your web browser ({brow_name}) is out of date.',
    msgmore: 'This site uses the latest technologies and thus needs a browser that is able to support such features. Update your browser for more security, speed and the best experience on this site.',
    bupdate: 'Update browser',
    bignore: 'Ignore',
    remind: 'You will be reminded in {days} days.'
  },
  test: false,
  dont_show_debuginfo: true
}

if ( typeof browserUpdate === 'undefined' ) {
  throw new Error( 'The plugin "browser-update" could not be loaded.' );
} else {
  browserUpdate( options, options.test );
}

// Content flyout/sidebar
const appStore = useApplicationStore( pinia );
const {
  contentModalVisible,
  isForbidden,
  isInternalError,
  isNotFound
} = storeToRefs( appStore );
const { isAuthenticated } = storeToRefs( useAuthStore() );
const layoutComponent = computed( () => isAuthenticated.value ? PrivateLayout : PublicLayout );
const isAdmin = computed< boolean >( () => appStore.isAdminSession() );
const showAdminBar = computed< boolean >( () => isAdmin.value || !!( localStorage.getItem( 'is-admin' ) ) );

function closeContentModal() {
  appStore.clearSelectedContent();
}

const { width } = useWindowSize();
const smallScreenSize = computed< boolean >( () => width.value < 768 );

// Completely remove all service-worker registrations for the given domain
if ( navigator.serviceWorker ) {
  // @ts-ignore
  const { clients } = self as ServiceWorkerGlobalScope;
  const swContainer: ServiceWorkerContainer = navigator.serviceWorker;
  swContainer
      .getRegistrations()
      .then( ( registrations: readonly ServiceWorkerRegistration[] ) => {
        try {
          for ( let registration of registrations ) {
            registration
                .unregister()
                .then( async () => {
                  return !clients ? [] : await clients.matchAll();
                } )
                .then( ( clients: readonly Client[] ) => {
                  clients.forEach( ( client: Client ) => {
                    if ( client.url && "navigate" in client ) {
                      // @ts-ignore
                      client.navigate( client.url );
                    }
                  } );
                } );
          }
        }
        catch ( error ) {
          console.warn( `De-registration failed with ${ error }` );
        }
      } );
}

const configurationFault = ref< boolean >( false );
onErrorCaptured( ( err: any ) => {
  if ( !configurationFault.value ) {
    clientStateManager.queueClientError( err, {} );

    // This keeps the browser from throwing multiple errors caused by the root issue of a configuration issue
    if ( err instanceof ConfigurationError ) {
      configurationFault.value = true;
    }
  }

  // Don't allow the error to bubble up any further
  return false;
} );

onMounted( () => {
  window.addEventListener( "beforeunload", appStore.terminateWorker );
  window.addEventListener( "unhandledrejection", appStore.handleRejectedPromise );

  // Initialize periodic alerts fetch
  const alertsStore = useApplicationAlertsStore();
  alertsStore.startPeriodicAlertsFetch();
} );

onBeforeUnmount( () => {
  window.removeEventListener( "beforeunload", appStore.terminateWorker );
  window.removeEventListener( "unhandledrejection", appStore.handleRejectedPromise );
} );

( async () => {
  await appStore.initializeAsync();
} )();
</script>

<style lang="scss">
.v-enter-active,
.v-leave-active {
  transition: opacity 0.5s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}

.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.75s ease-out;
}

.slide-down-enter-from {
  opacity: 0;
  transform: translateY(-30px);
}

.slide-down-leave-to {
  opacity: 0;
  transform: translateY(30px);
}
</style>
